import * as React from 'react'
import handleViewport from 'react-in-viewport'

import { i18n, withCacheConfiguration } from '@thg-commerce/enterprise-core'
import { CarouselButtonPlacement } from '@thg-commerce/gravity-patterns'
import { mq, spacing, styled, theme } from '@thg-commerce/gravity-theme'
import { ESIComponent, withESIWrapper } from '@thg-commerce/enterprise-esi'
import { ESIRequestContext } from '@thg-commerce/enterprise-esi/src/types'
import {
  Country,
  Currency,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  FlagshipWidgetInterface,
  ProductBlockPropsWithBeacons,
} from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Query/Widget/FlagshipWidget'
import { FlagshipWidget as FLAGSHIP_WIDGET_QUERY } from '@thg-commerce/enterprise-components/SponsoredAds/graphql/FlagshipWidget.graphql'
import {
  Orientation,
  ProductsShowcase,
  ProductsShowcaseProps,
} from '@thg-commerce/enterprise-widget-products-showcase'
import { Beacon } from '@thg-commerce/enterprise-network/src/transformers/sponsoredAds/products'

const StyledProductsShowcase = styled(ProductsShowcase)<{
  borderColour?: string
}>`
  margin: 0 auto;
  max-width: ${(props) => props.theme.site.siteWidth};
  border: 1px solid
    ${(props) => props.borderColour || theme.colors.palette.greys.light};
`

const RenderWrapper = styled.div`
  padding: 0;
  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    padding: ${spacing(3)};
  }
`

interface SponsoredAdsBannerProps {
  id: string
  path: string
}

interface RenderOptions {
  optionalFooterRedirectTarget: string
  mobileBackgroundImageAltText: string
  imageClickable: string
  optionalFooterType: string
  optionalFooterMediaFile: string
  desktopBackgroundImage: string
  optionalFooterRedirectUrl: string
  desktopBackgroundImageAltText: string
  mobileBackgroundImage: string
  borderColor: string
  optionalFooterRedirectUrlApp: string
}

interface BannerSponsoredAdsInitialProps {
  productListSponsoredAds?: ProductBlockPropsWithBeacons[]
  bannerBeacons?: {
    onLoadBeacon: Beacon
    onViewBeacon: Beacon
    onClickBeacon: Beacon
  }
  renderingOptions?: RenderOptions
}

const SponsoredAdsBanner: ESIComponent<
  SponsoredAdsBannerProps & BannerSponsoredAdsInitialProps,
  BannerSponsoredAdsInitialProps
> = (props) => {
  if (!props.productListSponsoredAds) {
    return null
  }

  const sponsoredSubtitleText = i18n('sponsored.banner.subtitle.text')

  const productsShowcaseProps: ProductsShowcaseProps = {
    image: {
      alt: props.renderingOptions?.desktopBackgroundImageAltText,
      source: props.renderingOptions?.desktopBackgroundImage || '',
      imageUrl: props.renderingOptions?.optionalFooterRedirectUrlApp,
      style: {
        imageStyle: 'cover',
        width: '100%',
        height: '100%',
      },
    },
    mobileImage: {
      alt: props.renderingOptions?.mobileBackgroundImageAltText,
      source: props.renderingOptions?.mobileBackgroundImage || '',
      imageUrl: props.renderingOptions?.optionalFooterRedirectUrlApp,
    },
    productList: props.productListSponsoredAds,
    subtitle: {
      text: sponsoredSubtitleText,
      subtitleTextAlign: 'right',
      margin: `${spacing(1)} ${spacing(1)} 0`,
    },
    orientation: Orientation.HORIZONTAL,
    carouselView: true,
    contentAlignments: {
      gridColumns: 12,
      gridItemColumns: {
        firstItem: {
          colStart: 1,
          colSpan: [12, 5, 5, 5],
          rowStart: [1, 1, 1, 1],
        },
        secondItem: {
          colStart: [1, 6, 6, 6],
          colSpan: [12, 7, 7, 7],
          rowStart: [2, 1, 1, 1],
        },
      },
      productContent: {
        titleAlignment: 'left',
        directions: ['row'],
      },
    },
    carouselControlsPosition: CarouselButtonPlacement.BottomRight,
    isSponsoredAdsBanner: true,
    carouselLoopSlides: true,
    sponsoredAdsBeacons: props.bannerBeacons,
    removeATags: true,
  }

  const ProductShowCaseInViewport = handleViewport(StyledProductsShowcase)

  return (
    <RenderWrapper>
      <ProductShowCaseInViewport
        {...productsShowcaseProps}
        borderColour={props.renderingOptions?.borderColor}
      />
    </RenderWrapper>
  )
}

SponsoredAdsBanner.getInitialProps = withCacheConfiguration<
  BannerSponsoredAdsInitialProps,
  ESIRequestContext<BannerSponsoredAdsInitialProps & SponsoredAdsBannerProps>
>(async (context) => {
  const { publicRuntimeConfig } = context.config
  const { siteDefinition } = publicRuntimeConfig

  const shippingDestination = (context.req.config.sessionSettings
    ?.shippingDestination ||
    siteDefinition.defaultSessionSettings.shippingDestination) as Country
  const currency = (context.req.config.sessionSettings?.currency ||
    siteDefinition.defaultSessionSettings.currency) as Currency
  const path = context.props.path
  const id = context.props.id

  try {
    const { data } = await context.apolloClient.query<{
      flagshipWidget: FlagshipWidgetInterface
    }>({
      query: FLAGSHIP_WIDGET_QUERY,
      variables: {
        path,
        currency,
        shippingDestination,
        id,
      },
    })

    if (!data) {
      return {}
    }

    const productListSponsoredAds = data?.flagshipWidget?.SponsoredAdsPlacement
    const bannerBeacons = data?.flagshipWidget?.bannerBeacons
    const renderingOptions = data?.flagshipWidget?.render

    return {
      ...context.props,
      productListSponsoredAds,
      bannerBeacons,
      renderingOptions,
      cache: {
        ttl: 0,
      },
    }
  } catch (error) {
    console.warn(`Sponsored ads Banner Flagship Terra error: ${error}`)
  }
  return {}
})

const ESISponsoredAdsBanner = withESIWrapper(SponsoredAdsBanner)

export { ESISponsoredAdsBanner as SponsoredAdsBanner }

export default ESISponsoredAdsBanner
